// ══════════════════════════════════════════════════════════════════════════════
// STORIES COMPONENT - Stories éphémères 7 jours avec texte/emojis
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  X, Plus, ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX,
  Eye, Clock, BadgeCheck, Camera, Type, Smile, Send, Loader2,
  Package, Wrench, ExternalLink
} from 'lucide-react';
import { API_URL } from '../../config/api';
import { TrustBadgeIcon } from '../badges/TrustBadge';
import { FounderBadgeIcon } from '../badges/FounderBadge';

// ══════════════════════════════════════════════════════════════════════════════
// CERCLE STORY - Avatar circulaire avec bordure
// ══════════════════════════════════════════════════════════════════════════════

function StoryCircle({ group, isAdd, onClick, hasUnviewed = true }) {
  const author = group?.author || {};
  
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center flex-shrink-0"
      style={{ width: '72px' }}
      data-testid={isAdd ? "add-story-btn" : `story-circle-${author.id}`}
    >
      <div 
        className={`w-[60px] h-[60px] rounded-full p-[2px] ${
          isAdd 
            ? 'border-2 border-dashed border-[#E8650A]' 
            : hasUnviewed 
              ? 'bg-gradient-to-tr from-[#E8650A] via-[#F97316] to-[#FBBF24]'
              : 'bg-gray-300'
        }`}
      >
        <div className="w-full h-full rounded-full bg-white p-[2px]">
          {isAdd ? (
            <div className="w-full h-full rounded-full bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
              <Plus size={22} className="text-[#E8650A]" />
            </div>
          ) : (
            <img
              src={author.profile_photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(author.display_name || 'A')}&background=1E3A5F&color=fff`}
              alt={author.display_name}
              className="w-full h-full rounded-full object-cover"
            />
          )}
        </div>
      </div>
      <span className="text-[10px] text-gray-700 font-medium w-full truncate text-center mt-1">
        {isAdd ? 'Ta story' : (author.display_name || '').split(' ')[0]}
      </span>
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// VIEWER STORY - Plein écran avec textes/emojis
// ══════════════════════════════════════════════════════════════════════════════

function StoryViewer({ groups, initialGroupIndex, onClose }) {
  const [groupIndex, setGroupIndex] = useState(initialGroupIndex);
  const [storyIndex, setStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);
  const progressInterval = useRef(null);
  
  const currentGroup = groups[groupIndex];
  const currentStory = currentGroup?.stories?.[storyIndex];
  const author = currentGroup?.author || {};
  
  const STORY_DURATION = 5000; // 5 secondes par photo
  
  // Progression automatique
  useEffect(() => {
    if (!currentStory || isPaused) return;
    
    const isVideo = currentStory.media_type === 'video';
    if (isVideo) return; // La vidéo gère sa propre progression
    
    setProgress(0);
    const startTime = Date.now();
    
    progressInterval.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = (elapsed / STORY_DURATION) * 100;
      
      if (newProgress >= 100) {
        nextStory();
      } else {
        setProgress(newProgress);
      }
    }, 50);
    
    return () => clearInterval(progressInterval.current);
  }, [groupIndex, storyIndex, isPaused, currentStory]);
  
  // Mark story as viewed
  useEffect(() => {
    if (currentStory?.id) {
      fetch(`${API_URL}/api/reseau/stories/${currentStory.id}/view`, { method: 'POST' })
        .catch(() => {});
    }
  }, [currentStory?.id]);
  
  const nextStory = useCallback(() => {
    if (storyIndex < (currentGroup?.stories?.length || 0) - 1) {
      setStoryIndex(prev => prev + 1);
      setProgress(0);
    } else if (groupIndex < groups.length - 1) {
      setGroupIndex(prev => prev + 1);
      setStoryIndex(0);
      setProgress(0);
    } else {
      onClose();
    }
  }, [storyIndex, groupIndex, currentGroup, groups.length, onClose]);
  
  const prevStory = useCallback(() => {
    if (storyIndex > 0) {
      setStoryIndex(prev => prev - 1);
      setProgress(0);
    } else if (groupIndex > 0) {
      setGroupIndex(prev => prev - 1);
      const prevGroup = groups[groupIndex - 1];
      setStoryIndex((prevGroup?.stories?.length || 1) - 1);
      setProgress(0);
    }
  }, [storyIndex, groupIndex, groups]);
  
  const handleTap = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const isLeftSide = x < rect.width / 3;
    
    if (isLeftSide) {
      prevStory();
    } else {
      nextStory();
    }
  };
  
  const handleVideoTimeUpdate = () => {
    if (videoRef.current) {
      const pct = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(pct);
    }
  };
  
  const handleVideoEnd = () => {
    nextStory();
  };
  
  if (!currentStory) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
      data-testid="story-viewer"
    >
      {/* Container story (aspect 9:16) */}
      <div 
        className="relative w-full h-full max-w-[430px] bg-black"
        onClick={handleTap}
        onMouseDown={() => setIsPaused(true)}
        onMouseUp={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        {/* Progress bars */}
        <div className="absolute top-2 left-2 right-2 z-20 flex gap-1">
          {currentGroup?.stories?.map((_, idx) => (
            <div key={idx} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white transition-all duration-100"
                style={{ 
                  width: idx < storyIndex ? '100%' : idx === storyIndex ? `${progress}%` : '0%' 
                }}
              />
            </div>
          ))}
        </div>
        
        {/* Header */}
        <div className="absolute top-6 left-0 right-0 z-20 px-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={author.profile_photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(author.display_name || 'A')}&background=1E3A5F&color=fff`}
              alt=""
              className="w-9 h-9 rounded-full border-2 border-white object-cover"
            />
            <div>
              <div className="flex items-center gap-1">
                <span className="text-white font-semibold text-sm">{author.display_name}</span>
                {author.is_verified && <BadgeCheck size={14} className="text-blue-400 fill-blue-400" />}
                {author.is_founder && <FounderBadgeIcon founderNumber={author.founder_number} size={14} />}
                {author.trust_level && <TrustBadgeIcon trustLevel={author.trust_level} size={14} />}
              </div>
              <span className="text-white/70 text-xs">{currentStory.time_ago || 'Récent'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {currentStory.media_type === 'video' && (
              <button
                onClick={(e) => { e.stopPropagation(); setIsMuted(!isMuted); }}
                className="p-2 text-white/80 hover:text-white"
              >
                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="p-2 text-white/80 hover:text-white"
              data-testid="close-story-btn"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        {/* Media */}
        <div className="absolute inset-0">
          {currentStory.media_type === 'video' ? (
            <video
              ref={videoRef}
              src={currentStory.media_url}
              poster={currentStory.thumbnail_url}
              className="w-full h-full object-cover"
              autoPlay
              playsInline
              muted={isMuted}
              onTimeUpdate={handleVideoTimeUpdate}
              onEnded={handleVideoEnd}
            />
          ) : (
            <img
              src={currentStory.media_url}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </div>
        
        {/* Text overlays */}
        {currentStory.text_overlays?.map((overlay) => (
          <div
            key={overlay.id}
            className="absolute z-10 pointer-events-none"
            style={{
              left: `${overlay.x}%`,
              top: `${overlay.y}%`,
              transform: `translate(-50%, -50%) rotate(${overlay.rotation || 0}deg)`,
            }}
          >
            <span
              style={{
                fontSize: `${overlay.font_size || 24}px`,
                color: overlay.color || '#FFFFFF',
                backgroundColor: overlay.background_color || 'transparent',
                fontWeight: overlay.font_style === 'bold' ? 'bold' : 'normal',
                fontStyle: overlay.font_style === 'italic' ? 'italic' : 'normal',
                padding: overlay.background_color ? '6px 12px' : '0',
                borderRadius: overlay.background_color ? '8px' : '0',
                textShadow: !overlay.background_color ? '0 2px 4px rgba(0,0,0,0.5)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {overlay.text}
            </span>
          </div>
        ))}
        
        {/* Lien Produit/Prestation */}
        {(currentStory.linked_product || currentStory.linked_service) && (
          <Link
            to={currentStory.linked_product 
              ? `/produit/${currentStory.linked_product.product_id || currentStory.linked_product.id}`
              : `/prestation/${currentStory.linked_service.prestation_id || currentStory.linked_service.id}`
            }
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-28 left-4 right-4 z-20 flex items-center gap-3 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg"
          >
            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
              {currentStory.linked_product?.image_url ? (
                <img 
                  src={currentStory.linked_product.image_url} 
                  alt="" 
                  className="w-full h-full object-cover"
                />
              ) : currentStory.linked_product ? (
                <Package size={20} className="text-gray-500" />
              ) : (
                <Wrench size={20} className="text-[#1E3A5F]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {currentStory.linked_product?.name || currentStory.linked_service?.name}
              </p>
              <p className="text-xs text-gray-500">
                {currentStory.linked_product 
                  ? `${currentStory.linked_product.price}€`
                  : `À partir de ${currentStory.linked_service.price_from}€`
                }
              </p>
            </div>
            <div className="flex items-center gap-1 text-[#E8650A] text-xs font-medium">
              <span>Voir</span>
              <ExternalLink size={14} />
            </div>
          </Link>
        )}
        
        {/* Caption */}
        {currentStory.caption && (
          <div className="absolute bottom-16 left-0 right-0 px-4 z-10">
            <p className="text-white text-sm text-center" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
              {currentStory.caption}
            </p>
          </div>
        )}
        
        {/* Footer - views */}
        <div className="absolute bottom-4 left-0 right-0 px-4 z-10 flex items-center justify-between">
          <div className="flex items-center gap-1 text-white/70 text-xs">
            <Eye size={14} />
            <span>{currentStory.views_count || 0} vues</span>
          </div>
          {currentStory.location && (
            <span className="text-white/70 text-xs">📍 {currentStory.location}</span>
          )}
        </div>
        
        {/* Navigation arrows (desktop) */}
        <button
          onClick={(e) => { e.stopPropagation(); prevStory(); }}
          className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 text-white/80 hover:bg-black/50 hidden md:flex"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); nextStory(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 text-white/80 hover:bg-black/50 hidden md:flex"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STORIES BAR - Barre horizontale des stories
// ══════════════════════════════════════════════════════════════════════════════

export default function StoriesBar({ onCreateStory }) {
  const [storyGroups, setStoryGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerStartIndex, setViewerStartIndex] = useState(0);
  const scrollRef = useRef(null);
  
  useEffect(() => {
    loadStories();
  }, []);
  
  const loadStories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reseau/stories`);
      if (res.ok) {
        const data = await res.json();
        setStoryGroups(data.story_groups || []);
      }
    } catch (e) {
      console.error('Error loading stories:', e);
    } finally {
      setLoading(false);
    }
  };
  
  const openStory = (index) => {
    setViewerStartIndex(index);
    setViewerOpen(true);
  };
  
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };
  
  if (loading) {
    return (
      <div className="bg-white py-1 border-b border-gray-100">
        <div className="flex px-1">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex flex-col items-center animate-pulse" style={{ width: '72px' }}>
              <div className="w-[60px] h-[60px] rounded-full bg-gray-200" />
              <div className="w-12 h-2 rounded bg-gray-200 mt-1" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <>
      <div className="bg-white py-1 border-b border-gray-100 relative" data-testid="stories-bar">
        {/* Scroll buttons (desktop) */}
        {storyGroups.length > 4 && (
          <>
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full bg-white shadow-md text-gray-600 hover:bg-gray-50 hidden md:flex"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full bg-white shadow-md text-gray-600 hover:bg-gray-50 hidden md:flex"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
        
        {/* Stories scroll container */}
        <div 
          ref={scrollRef}
          className="flex px-1 overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* Add story button */}
          <StoryCircle isAdd onClick={onCreateStory} />
          
          {/* Story circles */}
          {storyGroups.map((group, index) => (
            <StoryCircle
              key={group.author?.id || index}
              group={group}
              hasUnviewed={group.has_unviewed !== false}
              onClick={() => openStory(index)}
            />
          ))}
          
          {/* Empty state */}
          {storyGroups.length === 0 && (
            <div className="flex items-center gap-2 text-gray-400 text-sm pl-2">
              <Clock size={16} />
              <span>Aucune story récente</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Story Viewer Modal */}
      <AnimatePresence>
        {viewerOpen && storyGroups.length > 0 && (
          <StoryViewer
            groups={storyGroups}
            initialGroupIndex={viewerStartIndex}
            onClose={() => setViewerOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
